import type { Rect, Size } from '@/common/utils/formOverlayLayout'
import { clampDragMove } from '@/common/utils/formOverlayLayout'
import {
  hasOverlayDragThreshold,
  isOverlayDragExcludedTarget,
  OVERLAY_DRAG_HANDLE_SELECTOR,
  type FloatingOverlayDragState,
} from '@/common/utils/floatingOverlayDrag'
import { onUnmounted, watchEffect, type Ref } from 'vue'

export interface UseFloatingOverlayDragOptions {
  enabled: Ref<boolean>
  overlayEl: Ref<HTMLElement | null>
  getHostRect: () => Rect
  x: Ref<number>
  y: Ref<number>
  width: Ref<number>
  height: Ref<number>
  magnetic: Ref<number>
}

/**
 * Drag for floating form overlays (SwingOverlayPage).
 * Dialog overlays (`isDialog=true`) use `usePlatformDialogDrag` instead.
 */
export function useFloatingOverlayDrag(options: UseFloatingOverlayDragOptions) {
  let dragStart: FloatingOverlayDragState | null = null
  let isDragging = false
  let boundEl: HTMLElement | null = null

  const getSize = (): Size => ({
    width: Math.max(options.width.value, 1),
    height: Math.max(options.height.value, 1),
  })

  const finishDrag = (event: PointerEvent) => {
    const el = options.overlayEl.value
    if (el?.hasPointerCapture(event.pointerId)) {
      el.releasePointerCapture(event.pointerId)
    }
    if (isDragging) {
      event.preventDefault()
      event.stopPropagation()
    }
    dragStart = null
    isDragging = false
  }

  const onPointerDown = (event: PointerEvent) => {
    if (!options.enabled.value) return
    const el = options.overlayEl.value
    if (!el) return

    const handle = (event.target as Element | null)?.closest(OVERLAY_DRAG_HANDLE_SELECTOR)
    if (!handle || !el.contains(handle)) return
    if (isOverlayDragExcludedTarget(event.target, { inDragHandle: true })) return

    const deferred = !!handle.closest('.p-accordionheader, .p-accordion-header')
    dragStart = {
      x: options.x.value,
      y: options.y.value,
      pointerX: event.clientX,
      pointerY: event.clientY,
      deferred,
    }
    isDragging = !deferred

    if (!deferred) {
      el.setPointerCapture(event.pointerId)
      event.preventDefault()
    }
  }

  const onPointerMove = (event: PointerEvent) => {
    if (!dragStart) return
    const el = options.overlayEl.value
    if (!el) return

    const dx = event.clientX - dragStart.pointerX
    const dy = event.clientY - dragStart.pointerY

    if (!isDragging) {
      if (!dragStart.deferred || !hasOverlayDragThreshold(dx, dy)) return
      isDragging = true
      el.setPointerCapture(event.pointerId)
      event.preventDefault()
    }

    if (!el.hasPointerCapture(event.pointerId)) return

    const result = clampDragMove(
      options.getHostRect(),
      { x: dragStart.x, y: dragStart.y },
      getSize(),
      { dx, dy },
      options.magnetic.value,
    )
    options.x.value = result.x
    options.y.value = result.y
    options.magnetic.value = result.magnetic
    event.preventDefault()
  }

  const onPointerUp = (event: PointerEvent) => {
    if (!dragStart) return
    finishDrag(event)
  }

  const attach = (el: HTMLElement | null) => {
    detach()
    if (!el) return
    boundEl = el
    el.addEventListener('pointerdown', onPointerDown)
    el.addEventListener('pointermove', onPointerMove)
    el.addEventListener('pointerup', onPointerUp)
    el.addEventListener('pointercancel', onPointerUp)
  }

  const detach = () => {
    if (!boundEl) return
    boundEl.removeEventListener('pointerdown', onPointerDown)
    boundEl.removeEventListener('pointermove', onPointerMove)
    boundEl.removeEventListener('pointerup', onPointerUp)
    boundEl.removeEventListener('pointercancel', onPointerUp)
    boundEl = null
    dragStart = null
    isDragging = false
  }

  watchEffect(() => {
    const el = options.overlayEl.value
    if (el && options.enabled.value) {
      attach(el)
    } else {
      detach()
    }
  })

  onUnmounted(detach)

  return { attach, detach }
}
